import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MockupQuizSession from '../components/quiz/MockupQuizSession';

const API = process.env.REACT_APP_API_URL;
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  accept: 'application/json',
});

/**
 * Quest: Complete 2 quizzes today.
 * Fetches 2 random unanswered / wrong-answered quizzes and runs them full-screen via MockupQuizSession.
 * Each answer is POSTed to /quizzes/{id}/submit which auto-increments the quest counter on backend.
 */
const QuestQuizSessionPage = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/client/daily-quests/quiz-session?limit=2`, { headers: authHeader() })
      .then((r) => (r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`)))
      .then((j) => {
        const data = j.data || [];
        if (data.length === 0) setError('Bạn đã làm hết các bài quiz có sẵn. Quay lại sau nhé!');
        setQuizzes(data);
      })
      .catch(() => setError('Không tải được quiz. Thử lại sau.'))
      .finally(() => setLoading(false));
  }, []);

  // Submit answer to backend — triggers quest counter +1 via check_and_update_quests hook
  const submitToBackend = async (quiz, answer, localCorrect) => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    try {
      const et = quiz.exercise_type ? `?exercise_type=${encodeURIComponent(quiz.exercise_type)}` : '';
      await fetch(`${API}/quizzes/${quiz.id}/submit${et}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_answer: Array.isArray(answer) ? answer.join(',') : String(answer),
        }),
      });
    } catch (e) {
      // Silent — UI already shows local result; quest sync best-effort
    }
  };

  const handleExit = () => navigate('/dashboard');

  // ───── Loading ─────
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-slate-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6 text-center max-w-sm">
          <div className="inline-block w-10 h-10 border-4 border-hupath-scout/30 border-t-hupath-scout rounded-full animate-spin mb-4" />
          <p className="font-headline font-black text-hp-on-surface">Đang chuẩn bị quiz...</p>
        </div>
      </div>
    );
  }

  // ───── Error / empty ─────
  if (error || quizzes.length === 0) {
    return (
      <div className="fixed inset-0 bg-gradient-to-b from-slate-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] border-l-8 border-hupath-scout p-6 text-center max-w-sm">
          <div className="text-5xl mb-3">🎯</div>
          <h2 className="font-headline font-black text-xl text-hp-on-surface mb-2">Hết quiz rồi!</h2>
          <p className="text-hp-on-surface/60 text-sm mb-5">{error || 'Không có quiz để làm lúc này.'}</p>
          <button
            onClick={handleExit}
            className="hp-button-gradient text-white px-6 py-3 rounded-xl font-headline font-black shadow-lg hover:-translate-y-1 active:scale-95 transition-all"
          >
            ← Về Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ───── Active session (full-screen) ─────
  return (
    <MockupQuizSession
      quizzes={quizzes}
      open={true}
      onClose={handleExit}
      onSubmit={submitToBackend}
    />
  );
};

export default QuestQuizSessionPage;
